import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { teboxDesignSystem, useBookingTebox } from '../../styles/tebox-booking';
import {
  customerMobilePassUrl,
  openCustomerMobilePass,
  type MobilePassKind,
} from '../../services/mobile-passes';

type WalletAddButtonsProps = {
  /** Reservation passes — synchronous handlers (same pattern as confirmation modal). */
  onAppleClick?: () => void;
  onGoogleClick?: () => void;
  /** Gift card / house account — access token from API enables a native Wallet download link. */
  mobilePassResource?: {
    kind: Exclude<MobilePassKind, 'reservation'>;
    id: string;
    accessToken: string;
  };
  title?: string;
  embedded?: boolean;
  disabled?: boolean;
};

function AppleMark({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}

function GoogleWalletMark({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <path fill="#4285F4" d="M6 4h6v16H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
      <path fill="#34A853" d="M10 4h5v16h-5V4z" />
      <path fill="#FBBC04" d="M13 4h5a2 0 0 1 2 2v12a2 2 0 0 1-2 2h-5V4z" />
      <path fill="#EA4335" d="M18 6v12a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2z" />
    </svg>
  );
}

function RowContent({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: 'var(--brand-text, #021B1B)',
        }}
      >
        {icon}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
      <ChevronRight size={16} strokeWidth={2} aria-hidden style={{ flexShrink: 0, opacity: 0.45 }} />
    </>
  );
}

export const WalletAddButtons: React.FC<WalletAddButtonsProps> = ({
  onAppleClick,
  onGoogleClick,
  mobilePassResource,
  title,
  embedded = false,
  disabled = false,
}) => {
  const tebox = useBookingTebox();
  const fontFamily = 'var(--brand-font)';
  const shellRadius = 'var(--brand-border-radius, 6px)';
  const [applePassError, setApplePassError] = useState<string | null>(null);

  const applePassUrl = useMemo(() => {
    if (!mobilePassResource) return null;
    return customerMobilePassUrl(
      mobilePassResource.kind,
      mobilePassResource.id,
      'apple',
      mobilePassResource.accessToken,
    );
  }, [mobilePassResource]);

  useEffect(() => {
    if (!applePassUrl || disabled) {
      setApplePassError(null);
      return;
    }

    let cancelled = false;
    setApplePassError(null);

    void fetch(applePassUrl, { method: 'HEAD' })
      .then(async (response) => {
        if (cancelled) return;
        const contentType = response.headers.get('content-type') || '';
        if (response.ok && contentType.includes('application/vnd.apple.pkpass')) {
          return;
        }
        let message = 'Apple Wallet pass is not available yet.';
        try {
          const bodyResponse = await fetch(applePassUrl);
          const parsed = (await bodyResponse.json()) as { error?: string; message?: string; code?: string };
          message = parsed.error || parsed.message || message;
          if (parsed.code === 'MOBILE_PASS_PRODUCTION_NOT_CONFIGURED') {
            message = 'Gift card and wallet passes are not configured on this server yet.';
          }
        } catch {
          /* keep default */
        }
        setApplePassError(message);
      })
      .catch(() => {
        if (!cancelled) setApplePassError('Could not reach the pass server.');
      });

    return () => {
      cancelled = true;
    };
  }, [applePassUrl, disabled]);

  const rowStyle = (isLast: boolean, isDisabled = disabled): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: teboxDesignSystem.spacing.sm,
    width: '100%',
    minHeight: 48,
    padding: `${teboxDesignSystem.spacing.sm} ${teboxDesignSystem.spacing.md}`,
    border: 'none',
    borderBottom: isLast ? 'none' : `1px solid ${tebox.colors.border}`,
    background: teboxDesignSystem.colors.background.primary,
    color: 'var(--brand-text, #021B1B)',
    fontFamily,
    fontSize: teboxDesignSystem.typography.body.fontSize,
    fontWeight: 500,
    lineHeight: teboxDesignSystem.typography.body.lineHeight,
    textAlign: 'left',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.55 : 1,
    transition: 'background-color 0.15s ease',
    textDecoration: 'none',
    boxSizing: 'border-box',
  });

  const handleGoogleClick = () => {
    if (mobilePassResource) {
      void openCustomerMobilePass(mobilePassResource.kind, mobilePassResource.id, 'google');
      return;
    }
    onGoogleClick?.();
  };

  const appleLabel = applePassError
    ? applePassError
    : applePassUrl
      ? 'Add to Apple Wallet'
      : 'Apple Wallet unavailable';

  const appleRow =
    applePassUrl && !applePassError && !disabled ? (
      <a href={applePassUrl} aria-label="Add to Apple Wallet" style={rowStyle(false)}>
        <RowContent icon={<AppleMark />} label="Add to Apple Wallet" />
      </a>
    ) : (
      <button
        type="button"
        disabled
        aria-label={appleLabel}
        style={rowStyle(false, true)}
      >
        <RowContent icon={<AppleMark />} label={appleLabel} />
      </button>
    );

  const reservationAppleRow = (
    <button
      type="button"
      disabled={disabled}
      aria-label="Add to Apple Wallet"
      onClick={onAppleClick}
      style={rowStyle(false)}
    >
      <RowContent icon={<AppleMark />} label="Add to Apple Wallet" />
    </button>
  );

  const list = (
    <div
      style={
        embedded
          ? { display: 'flex', flexDirection: 'column', width: '100%' }
          : {
              width: '100%',
              maxWidth: 360,
              borderRadius: shellRadius,
              border: `1px solid ${tebox.colors.border}`,
              overflow: 'hidden',
              backgroundColor: teboxDesignSystem.colors.background.primary,
            }
      }
    >
      {mobilePassResource ? appleRow : reservationAppleRow}

      <button
        type="button"
        disabled={disabled}
        aria-label="Add to Google Wallet"
        onClick={handleGoogleClick}
        style={rowStyle(true)}
      >
        <RowContent icon={<GoogleWalletMark />} label="Add to Google Wallet" />
      </button>
    </div>
  );

  if (embedded) {
    return list;
  }

  return (
    <div style={{ width: '100%', maxWidth: 360 }}>
      {title ? (
        <p
          style={{
            ...teboxDesignSystem.typography.caption,
            fontFamily,
            color: tebox.colors.textSecondary,
            margin: `0 0 ${teboxDesignSystem.spacing.sm}`,
          }}
        >
          {title}
        </p>
      ) : null}
      {list}
    </div>
  );
};
